/**
 * @file: ChatConversation.tsx
 *
 * The conversation surface — a thin ORCHESTRATOR. It composes the wired
 * pieces (message list, composer, scroll-to-bottom pill, image preview,
 * read-by sheet), each of which owns its own state next to its pure
 * component.
 *
 * Header: when the conversation OWNS its header (a host that passes
 * `onOpenRoomInfo` — the desktop pane), it renders `WiredChatConversationHeader`.
 * When the host owns the header (the mobile navigator), no `onOpenRoomInfo`
 * is passed and the conversation renders no header — the host drives the
 * navigator title via `TypingIndicator`. Either way the conversation knows
 * nothing about WHERE it's rendered.
 */

import { Typography } from '@euler/app/components/typography';
import { WiredComposer } from '@euler/app/flows/chat/components/composer/WiredComposer';
import { useChatConversation } from '@euler/app/flows/chat/lib/conversation/context';
import { isBaked } from '@euler/app/flows/chat/lib/conversation/handle';
import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { useServiceFactory } from '@euler/services/factory';
import { idEquals } from '@euler/utils';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WiredBubbleMenu } from './WiredBubbleMenu';
import { WiredChatConversationHeader } from './WiredChatConversationHeader';
import { WiredImagePreview } from './WiredImagePreview';
import { WiredMessageList } from './WiredMessageList';
import { WiredMessageReadersSheet } from './WiredMessageReadersSheet';
import { WiredScrollToBottomPill } from './WiredScrollToBottomPill';

export const ChatConversation: FC<{
  onOpenRoomInfo?: () => void;
  /** Called after the viewer deletes a closed (removed/dismissed) thread —
   * host pops back / deselects the pane. */
  onExit?: () => void;
}> = memo(({ onOpenRoomInfo, onExit }) => {
  // The room is read-only once it's dismissed (a `room.dismissed`
  // notification) OR once I'M removed from it (a `members.removed` whose
  // `removed` list includes me — NOT when someone else is removed). Both
  // land as notifications in the stream. When closed, hide the composer and
  // make every message non-interactive.
  const { messages$, conversationHandle } = useChatConversation();
  const [messages] = useBehaviorSubject(messages$);
  const { me, setRoomClosed } = useChatSession();
  const myId = me.platformUserId;
  const closedReason = useMemo<'dismissed' | 'removed' | null>(() => {
    // Dismissal is terminal for everyone.
    if (
      messages.some(
        m =>
          m.payload.type === 'notification' &&
          m.payload.notificationType === 'room.dismissed',
      )
    ) {
      return 'dismissed';
    }
    // Otherwise the LATEST membership event for me decides: a `members.removed`
    // closes the room; a later `members.invited` re-adding me re-opens it (so
    // a re-invite after a removal isn't stuck read-only).
    let latest: { at: string; removed: boolean } | null = null;
    for (const m of messages) {
      if (m.payload.type !== 'notification') continue;
      const p = m.payload;
      const removed: boolean | null =
        p.notificationType === 'members.removed' &&
        (p.removed ?? []).some(r => idEquals(r.platformUserId, myId))
          ? true
          : p.notificationType === 'members.invited' &&
            (p.invitees ?? []).some(i => idEquals(i.platformUserId, myId))
          ? false
          : null;
      if (removed === null) continue;
      if (!latest || m.createdAt > latest.at) {
        latest = { at: m.createdAt, removed };
      }
    }
    return latest?.removed ? 'removed' : null;
  }, [messages, myId]);
  const closed = closedReason != null;

  // Publish closure to the session so chrome OUTSIDE this provider (the mobile
  // nav header's `⋮`, the desktop info column) can suppress the room-detail
  // entry, which 404s for a non-member. Cleared when re-opened (re-invited).
  const roomId = isBaked(conversationHandle) ? conversationHandle.roomId : null;
  useEffect(() => {
    if (roomId) setRoomClosed(roomId, closed);
  }, [closed, roomId, setRoomClosed]);

  // The host owns the header iff it passed `onOpenRoomInfo` (desktop pane).
  // Keep the header in that case, but drop the room-info `⋮` once closed —
  // the room detail isn't reachable for a removed/dismissed viewer.
  const ownsHeader = onOpenRoomInfo != null;

  return (
    <View style={{ flex: 1 }}>
      <View style={{ flex: 1, backgroundColor: '#fafafa' }}>
        {ownsHeader ? (
          <WiredChatConversationHeader
            onOpenRoomInfo={closed ? undefined : onOpenRoomInfo}
          />
        ) : null}

        <WiredMessageList interactive={!closed} />

        {/*
         * Typing lives in the header subtitle (WeChat-style), so the bottom
         * stack carries no layout shifts that would push the pill upward.
         * The compose area (reply preview + input strip) follows — replaced
         * by a passive notice once the room is closed (dismissed / I was
         * removed). The notice carries a delete action (the room detail is
         * the usual delete entry, but it's unreachable when closed).
         */}
        {closed ? (
          <ClosedNotice reason={closedReason} onExit={onExit} />
        ) : (
          <WiredComposer />
        )}
        <WiredScrollToBottomPill />
      </View>

      <WiredImagePreview />
      <WiredMessageReadersSheet />
      <WiredBubbleMenu />
    </View>
  );
});

/**
 * Replaces the composer in a closed room: the reason + a 删除聊天 action. The
 * room detail (the usual delete entry) is unreachable once closed, so the
 * delete lives here — wipes the viewer's thread, then the host exits.
 */
const ClosedNotice: FC<{
  reason: 'dismissed' | 'removed' | null;
  onExit?: () => void;
}> = memo(({ reason, onExit }) => {
  const { rpx } = useResponsiveContext();
  const insets = useSafeAreaInsets();
  const { chatService } = useServiceFactory();
  const confirm = useConfirm();
  const { conversationHandle } = useChatConversation();
  const [busy, setBusy] = useState(false);
  const threadId = isBaked(conversationHandle)
    ? conversationHandle.threadId
    : null;

  const onDelete = useCallback(async () => {
    if (!threadId || busy) return;
    const ok = await confirm({
      title: '删除聊天',
      message: '将删除该会话及本地聊天记录，确定删除？',
      confirmButtonText: '删除',
      destructive: true,
    });
    if (!ok) return;
    try {
      setBusy(true);
      await chatService.deleteThread(threadId);
      onExit?.();
    } catch (e) {
      console.error('delete thread failed', e);
    } finally {
      setBusy(false);
    }
  }, [threadId, busy, confirm, chatService, onExit]);

  return (
    <View
      style={{
        backgroundColor: '#f0f0f0',
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: '#e0e0e0',
        paddingTop: rpx(14),
        paddingBottom: insets.bottom + rpx(14),
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography.Text size={rpx(13)} color="#9ca3af">
        {reason === 'removed' ? '你已被移出该群聊' : '该群聊已解散'}
      </Typography.Text>
      {threadId ? (
        <TouchableOpacity
          onPress={onDelete}
          disabled={busy}
          hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
          style={{ marginLeft: rpx(8), opacity: busy ? 0.5 : 1 }}
        >
          <Typography.Text size={rpx(13)} color="#3b82f6">
            删除聊天
          </Typography.Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
});
